// frontend/app/components/lessonPlans/LessonPhaseRail.js
//
// The persistent left rail from the V1 storyboard. Driven entirely by the
// lesson's own steps — a four-step or seven-step lesson renders correctly
// without a code change. No count is hardcoded anywhere in this file.
//
// Rows are grouped under the four teacher-facing sections (see
// lessonPlanRules.groupStepsBySection): a section with more than one step
// gets a heading row with its steps indented beneath it, each showing its
// display title (see lessonPlanRules.stepDisplayTitle: an authored title, else
// "Step N" — never a phase name); a section with exactly one step collapses
// into a single row carrying the section's own label, so a four-step lesson
// still reads as four rows rather than four headings each with one orphan.
//
// The Saras Learning Loop's seven steps are the reason the density rule below
// exists: at seven rows there is no room for generous padding on every row,
// and a step you cannot see is a step a teacher forgets. Every label still
// renders in full — nothing is truncated.

import React from "react";
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import {
  groupStepsBySection,
  stepDisplayTitle,
  stepIsComplete,
  stepProgress,
} from "./lessonPlanRules";

const COVER_INDEX = -1;

// From this many steps up, the rail tightens row padding so the lesson still
// fits the 260px rail column without scrolling the last step out of sight.
// Six, not five: a five-step lesson still fits comfortably.
const DENSE_STEP_COUNT = 6;

/** A non-interactive label row above the steps of a multi-step section. */
const SectionHeading = ({ meta, t }) => {
  const translate = t || ((value) => value);
  return (
    <View style={styles.sectionHeadingRow}>
      <MaterialCommunityIcons name={meta.icon} size={14} color={meta.accent} />
      <Text style={[styles.sectionHeadingText, { color: meta.accent }]}>
        {translate(meta.label)}
      </Text>
    </View>
  );
};

const RailRow = ({
  colors,
  index,
  step,
  isActive,
  complete,
  progress,
  dense,
  onPress,
  t,
  // The icon/accent (and, for a lone row, the label) of the section this row
  // belongs to. Phases are a hidden key and never appear on screen.
  identity,
  // What the row reads: the step's display title.
  displayTitle,
  indented,
}) => {
  const translate = t || ((value) => value);

  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected: isActive }}
      accessibilityLabel={`${index + 1}. ${displayTitle}`}
      style={[
        styles.row,
        dense ? styles.rowDense : null,
        indented ? styles.rowIndented : null,
        {
          backgroundColor: isActive ? colors.accentGlow : "transparent",
          borderColor: isActive ? colors.accentPrimary : "transparent",
        },
      ]}
    >
      <View
        style={[
          styles.marker,
          {
            backgroundColor: complete ? identity.accent : "transparent",
            borderColor: complete ? identity.accent : colors.borderStrong,
          },
        ]}
      >
        {complete ? (
          <MaterialCommunityIcons name="check" size={14} color="#ffffff" />
        ) : (
          <Text style={[styles.markerNumber, { color: colors.textSecondary }]}>
            {index + 1}
          </Text>
        )}
      </View>

      <View style={styles.rowBody}>
        <View style={styles.rowTitleRow}>
          <MaterialCommunityIcons
            name={identity.icon}
            size={13}
            color={identity.accent}
            style={styles.rowIcon}
          />
          <Text
            style={[
              styles.rowTitle,
              { color: isActive ? colors.textPrimary : colors.textSecondary },
            ]}
            numberOfLines={1}
          >
            {indented ? displayTitle : translate(identity.label)}
          </Text>
        </View>
        {/* A lone-section row is titled by its section; the step's own title
            is a subtitle, and only when it adds something. */}
        {!indented && displayTitle !== translate(identity.label) ? (
          <Text style={[styles.rowCaption, { color: colors.textMuted }]} numberOfLines={1}>
            {displayTitle}
          </Text>
        ) : null}

        {!complete && progress > 0 ? (
          <View style={[styles.progressTrack, { backgroundColor: colors.border }]}>
            <View
              style={[
                styles.progressFill,
                { backgroundColor: identity.accent, width: `${Math.round(progress * 100)}%` },
              ]}
            />
          </View>
        ) : null}
      </View>
    </TouchableOpacity>
  );
};

const LessonPhaseRail = ({
  steps = [],
  activeIndex,
  sessionState,
  colors,
  onSelect,
  onSelectCover,
  t,
}) => {
  const translate = t || ((value) => value);
  const dense = steps.length >= DENSE_STEP_COUNT;
  const sections = groupStepsBySection(steps);
  const completedSections = sections.filter((section) =>
    section.steps.every(({ step }) => stepIsComplete(step, sessionState))
  ).length;

  return (
    <View
      style={[
        styles.container,
        { backgroundColor: colors.railBg, borderColor: colors.border },
      ]}
    >
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <Text style={[styles.headerLabel, { color: colors.textMuted }]}>
          {translate("Lesson flow")}
        </Text>
        <Text style={[styles.headerValue, { color: colors.textPrimary }]}>
          {completedSections}/{sections.length} {translate("sections complete")}
        </Text>
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <TouchableOpacity
          onPress={onSelectCover}
          accessibilityRole="button"
          accessibilityState={{ selected: activeIndex === COVER_INDEX }}
          accessibilityLabel={translate("Lesson overview")}
          style={[
            styles.coverRow,
            {
              backgroundColor:
                activeIndex === COVER_INDEX ? colors.accentGlow : "transparent",
              borderColor:
                activeIndex === COVER_INDEX ? colors.accentPrimary : colors.border,
            },
          ]}
        >
          <MaterialCommunityIcons
            name="book-open-variant"
            size={16}
            color={colors.accentPrimary}
          />
          <Text style={[styles.coverText, { color: colors.textPrimary }]}>
            {translate("Lesson overview")}
          </Text>
        </TouchableOpacity>

        {sections.map((section) => {
          const lone = section.steps.length === 1;

          if (lone) {
            const { step, index } = section.steps[0];
            return (
              <RailRow
                key={step.step_key}
                colors={colors}
                index={index}
                step={step}
                isActive={index === activeIndex}
                complete={stepIsComplete(step, sessionState)}
                progress={stepProgress(step, sessionState)}
                dense={dense}
                onPress={() => onSelect(index)}
                t={t}
                identity={section.meta}
                displayTitle={stepDisplayTitle(step, 0, 1, translate)}
                indented={false}
              />
            );
          }

          return (
            <View key={section.key || "unsectioned"}>
              <SectionHeading meta={section.meta} t={t} />
              {section.steps.map(({ step, index }, indexInSection) => {
                return (
                  <RailRow
                    key={step.step_key}
                    colors={colors}
                    index={index}
                    step={step}
                    isActive={index === activeIndex}
                    complete={stepIsComplete(step, sessionState)}
                    progress={stepProgress(step, sessionState)}
                    dense={dense}
                    onPress={() => onSelect(index)}
                    t={t}
                    identity={section.meta}
                    displayTitle={stepDisplayTitle(
                      step,
                      indexInSection,
                      section.steps.length,
                      translate
                    )}
                    indented
                  />
                );
              })}
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, borderRadius: 16, borderWidth: 1, overflow: "hidden" },
  header: { paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1 },
  headerLabel: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.8,
    textTransform: "uppercase",
  },
  headerValue: { fontSize: 16, fontWeight: "700", marginTop: 4 },
  scroll: { flex: 1 },
  scrollContent: { padding: 10, gap: 4 },

  coverRow: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 8,
    gap: 8,
  },
  coverText: { fontSize: 13, fontWeight: "700", marginLeft: 8 },

  sectionHeadingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 6,
    paddingTop: 10,
    paddingBottom: 4,
  },
  sectionHeadingText: { fontSize: 12, fontWeight: "800" },

  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 10,
  },
  rowIndented: { marginLeft: 14 },
  marker: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1.5,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
    marginTop: 1,
  },
  rowDense: { paddingVertical: 8 },
  markerNumber: { fontSize: 11, fontWeight: "800" },
  rowBody: { flex: 1 },
  rowTitleRow: { flexDirection: "row", alignItems: "center" },
  rowIcon: { marginRight: 5 },
  rowTitle: { fontSize: 13, fontWeight: "700", flexShrink: 1 },
  rowCaption: { fontSize: 11, marginTop: 1 },
  progressTrack: {
    height: 3,
    borderRadius: 2,
    marginTop: 8,
    overflow: "hidden",
  },
  progressFill: { height: 3, borderRadius: 2 },
});

export default LessonPhaseRail;
